import type { InjectionKey } from 'vue';
import type { ParcelStatusType } from './dev-tools/constants';

/** Window key for the shell's public API surface */
export const SHELL_API_KEY = 'shell/v1/api' as const;

/** localStorage key for parcel override configuration */
export const PARCEL_OVERRIDE_STORAGE_KEY = 'abax:parcel-overrides' as const;

/** Shape of a Module Federation container registered on window */
export interface FederationContainer {
  /** Webpack containers expose init() for shared scope negotiation */
  init?: (shareScope: Record<string, unknown>) => Promise<void>;
  /** Returns a factory function for the requested module */
  get(module: string): Promise<() => unknown>;
}

/** single-spa parcel configuration object */
export interface ParcelConfig {
  name?: string;
  bootstrap?: (props: Record<string, unknown>) => Promise<void>;
  mount: (props: Record<string, unknown>) => Promise<void>;
  unmount: (props: Record<string, unknown>) => Promise<void>;
  update?: (props: Record<string, unknown>) => Promise<void>;
}

/** Mounted parcel instance returned by mountParcel */
export interface MountedParcel {
  mount(): Promise<void>;
  unmount(): Promise<void>;
  update?(props: Record<string, unknown>): Promise<void>;
  getStatus(): ParcelStatusType;
  loadPromise: Promise<void>;
  bootstrapPromise: Promise<void>;
  mountPromise: Promise<void>;
  unmountPromise: Promise<void>;
}

/** Function to mount a parcel into a DOM element */
export interface MountParcel {
  (
    config: ParcelConfig,
    customProps: Record<string, unknown> & { domElement: HTMLElement },
  ): MountedParcel;
}

/**
 * Vue injection key for single-spa mountParcel function.
 *
 * `Symbol.for(...)` keys the registry by string so the host's provider and
 * a federated parcel's `inject()` see the same symbol even when each
 * evaluates its own copy of `@abax/platform`. Module-local `Symbol(...)`
 * is unsafe across bundles — two evaluations produce two unequal symbols
 * of the same description, so `inject()` returns `undefined`.
 */
export const MOUNT_PARCEL_KEY: InjectionKey<MountParcel> = Symbol.for(
  '@abax/platform/parcels/MOUNT_PARCEL_KEY',
);

/**
 * Maps parcel scope names to their remoteEntry.js URLs.
 * Configured once per app, looked up by ParcelLoader when window[scope] isn't pre-loaded.
 */
export type ParcelRemotes = Record<string, string>;

/**
 * Vue injection key for the parcel remotes registry.
 *
 * See `MOUNT_PARCEL_KEY` for the cross-bundle rationale — `Symbol.for(...)`
 * keeps host and federated parcel copies aligned.
 */
export const PARCEL_REMOTES_KEY: InjectionKey<ParcelRemotes> = Symbol.for(
  '@abax/platform/parcels/PARCEL_REMOTES_KEY',
);

/** Schema version identifier for parcel-manifest.json forward compatibility */
export const PARCEL_MANIFEST_SCHEMA = 'parcel-manifest/v1' as const;

/** CustomEvent name dispatched when a federation container is loaded onto window */
export const CONTAINER_LOADED_EVENT = 'parcel:container-loaded' as const;

/** CustomEvent name dispatched when a federation container is invalidated (removed from window) */
export const CONTAINER_INVALIDATED_EVENT = 'parcel:container-invalidated' as const;

/** CustomEvent name dispatched when a federation container's entry script changes (hot reload) */
export const CONTAINER_CHANGED_EVENT = 'parcel:container-changed' as const;

/** CustomEvent name dispatched when a parcel's status changes */
export const STATUS_CHANGED_EVENT = 'parcel:status-changed' as const;

/** Detail payload for container lifecycle CustomEvents */
export interface ContainerLifecycleDetail {
  /** The federation scope key (e.g. '@abax/central/installer-ui') */
  scope: string;
  /** Timestamp of the event */
  timestamp: number;
}

/**
 * Metadata a parcel serves as `parcel-manifest.json` next to its remote entry.
 * Consumers use this to auto-discover parcel URLs instead of hardcoding them.
 */
export interface ParcelManifest {
  /** Schema version for forward compatibility */
  $schema: typeof PARCEL_MANIFEST_SCHEMA;
  /** Remote scope name (e.g. '@abax/central/installer-ui') */
  scope: string;
  /** Entry script filename (e.g. 'remoteEntry.js') */
  filename: string;
  /** Dev server port — set in dev mode, absent in production builds */
  devPort?: number;
  /** Absolute path to the project root on disk — used by orchestrator to locate source projects */
  projectRoot?: string;
  /** Exposed module names (e.g. ['./vehicle-installations', './send-installation-link']) */
  modules: string[];
  /** Widget descriptors registered by this parcel (keyed by widgetId) */
  widgets?: Record<string, unknown>;
}

/**
 * Contract for a parcel's lifecycle — what defineParcel returns (single-spa-vue v3).
 */
export interface ParcelContract<TProps extends Record<string, unknown> = Record<string, unknown>> {
  bootstrap?: (props: TProps) => Promise<void>;
  mount: (props: TProps) => Promise<void>;
  unmount: (props: TProps) => Promise<void>;
  update?: (props: TProps) => Promise<void>;
}
